import { h, ComponentChildren, ComponentType } from "preact";

import classname from "../../../../foundation/utils/classname";
import { exists } from "../../../../foundation/utils/object";

import IconProps from "../../../Icons/IconProps";
import IconCheck from "../../Icons/IconCheck";
import withMenuItem, { WithMenuItemProps } from "../withMenuItem";

import MenuButtonLabel from "./MenuButtonLabel";
import { MenuButtonLabelOverflowBehavior } from "./types";

import "../style.css";

interface Props extends WithMenuItemProps {
    class?: string;
    warning?: boolean;
    icon?: ComponentType<IconProps>;
    level?: number;
    children?: ComponentChildren;
    subContent?: string;
    inactionable?: boolean;
    checked?: boolean;
    rightContent?: ComponentChildren;
    overflowBehavior?: MenuButtonLabelOverflowBehavior;
    onClick?(ev: MouseEvent, value?: string, checked?: boolean): void;
    onMouseDown?(ev: MouseEvent, value?: string, checked?: boolean): void;
    onMouseEnter?: (value?: string) => void;
    onMouseLeave?: (value?: string) => void;
}

function MenuButton({
    class: cssClass,
    value,
    warning,
    icon: Icon,
    level,
    children,
    content,
    subContent,
    highlighted,
    inactionable,
    disabled,
    checked,
    rightContent,
    overflowBehavior = MenuButtonLabelOverflowBehavior.Ellipsis,
    menuItemRef,
    onClick,
    onMenuItemClick,
    onMouseEnter,
    onMouseDown,
    onMenuItemMouseEnter,
    onMouseLeave,
    onMenuItemMouseLeave,
    onMenuItemMouseMove
}: Props) {
    const checkable = exists(checked);
    const hasRightContent = checked || !!rightContent;
    const leveled = !!level;

    function handleClick(event: MouseEvent) {
        if (inactionable) {
            return;
        }

        onClick?.(event, value, checkable ? !checked : undefined);
        onMenuItemClick?.(event);
    }

    function handleMouseDown(event: MouseEvent) {
        event.stopPropagation();
        onMouseDown?.(event, value, checkable ? !checked : undefined);
    }

    function handleMouseEnter() {
        onMouseEnter?.(value);
        onMenuItemMouseEnter?.();
    }

    function handleMouseLeave() {
        onMouseLeave?.(value);
        onMenuItemMouseLeave?.();
    }

    function handleMouseMove() {
        onMenuItemMouseMove?.();
    }

    return (
        <button
            ref={menuItemRef}
            type="button"
            class={classname("menuButton body-1", cssClass, { warning, highlighted, hasRightContent, leveled })}
            disabled={disabled}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}>
            {Icon && (
                <div class="icon">
                    <Icon/>
                </div>
            )}
            <div
                class="contentContainer"
                style={level ? { marginLeft: `calc(${level} * var(--spacing-s))` } : undefined}>
                <MenuButtonLabel overflowBehavior={overflowBehavior}>{children ?? content}</MenuButtonLabel>
                {subContent && <span class="caption-1 subContent">{subContent}</span>}
            </div>
            {hasRightContent && (
                <div class="rightContent caption-1">
                    {checked ? <IconCheck class="tick"/> : rightContent}
                </div>
            )}
        </button>
    );
}

const MenuButtonAsMenuItem = withMenuItem(MenuButton);

export default MenuButton;
export { MenuButtonAsMenuItem };
